// src/finance/components/JurisdictionCompare.tsx
// Comparatif permanent « société imposée au UK vs en France » (spec 2026-08-20 § 2).
// L'écart est signé : positif = la France coûte plus cher dans le sens courant
// (CA requis supérieur en netToRevenue, net inférieur étant l'inverse en revenueToNet).
import React from 'react'
import type { FinanceDirection, RegimeResult } from '../engine/types'
import { fmtEur } from '../format'

export type CompareRow = {
  label: string
  uk: RegimeResult
  fr: RegimeResult
  /**
   * Juridictions où AUCUN CA n'atteint le net visé (cf. scenarios.ts). Leur
   * `RegimeResult` est un repli à zéro : en netToRevenue un CA de 0 € bat
   * mécaniquement tout CA réel, donc sans ce drapeau le repère « le plus
   * favorable » désignait la juridiction où la cible est IMPOSSIBLE, et l'écart
   * chiffrait la différence entre un résultat et un blanc.
   */
  unreachable?: { uk: boolean; fr: boolean } | undefined
}

const cell: React.CSSProperties = {
  padding: '0.4rem 0.625rem',
  fontSize: '0.8125rem',
  color: 'var(--fg)',
  borderBottom: '1px solid var(--line)',
  textAlign: 'right',
  fontFamily: 'var(--mono)',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
}
const headCell: React.CSSProperties = {
  ...cell,
  color: 'var(--muted)',
  fontFamily: 'inherit',
  fontWeight: 500,
  fontSize: '0.6875rem',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  borderBottom: '1.5px solid var(--fg)',
}

function metric(r: RegimeResult, direction: FinanceDirection): number {
  return direction === 'netToRevenue' ? r.revenueMonthly : r.netMonthly
}

export function JurisdictionCompare({
  direction,
  rows,
}: {
  direction: FinanceDirection
  rows: CompareRow[]
}) {
  const metricLabel = direction === 'netToRevenue' ? 'CA mensuel à facturer' : 'Net perso mensuel'
  // Favorable : CA le plus bas (netToRevenue) ou net le plus haut (revenueToNet).
  const out = (row: CompareRow, side: 'uk' | 'fr'): boolean => row.unreachable?.[side] === true
  const better = (row: CompareRow): 'uk' | 'fr' | null => {
    // Une juridiction hors de portée n'est comparable à rien : elle ne gagne pas,
    // et elle ne fait pas gagner l'autre par défaut non plus si les deux le sont.
    if (out(row, 'uk') && out(row, 'fr')) return null
    if (out(row, 'uk')) return 'fr'
    if (out(row, 'fr')) return 'uk'
    const uk = metric(row.uk, direction)
    const fr = metric(row.fr, direction)
    if (uk === fr) return null
    if (direction === 'netToRevenue') return uk < fr ? 'uk' : 'fr'
    return uk > fr ? 'uk' : 'fr'
  }
  const mark = (side: 'uk' | 'fr', row: CompareRow) =>
    better(row) === side ? (
      <span data-testid="compare-better" data-side={side} style={{ color: 'var(--bronze)' }}>
        {' '}
        ●
      </span>
    ) : null

  return (
    <section
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: '10px',
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.625rem',
      }}
    >
      <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--fg)', margin: 0 }}>
        Société imposée : UK vs France
      </h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...headCell, textAlign: 'left' }}>Mode</th>
              <th style={headCell}>{metricLabel} — UK</th>
              <th style={headCell}>France</th>
              <th style={headCell}>Écart</th>
              <th style={headCell}>Impôt société/an — UK</th>
              <th style={headCell}>France</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const ukOut = out(row, 'uk')
              const frOut = out(row, 'fr')
              const uk = metric(row.uk, direction)
              const fr = metric(row.fr, direction)
              const delta = fr - uk
              // Un tiret cadratin plutôt qu'un « 0 € » : le repli n'est pas un montant.
              const amount = (value: number, missing: boolean) => (missing ? '—' : fmtEur(value))
              return (
                <tr key={row.label}>
                  <td style={{ ...cell, textAlign: 'left', fontFamily: 'inherit' }}>{row.label}</td>
                  <td style={cell}>
                    {amount(uk, ukOut)}
                    {mark('uk', row)}
                  </td>
                  <td style={cell}>
                    {amount(fr, frOut)}
                    {mark('fr', row)}
                  </td>
                  <td style={{ ...cell, color: delta === 0 ? 'var(--dim)' : 'var(--muted)' }}>
                    {ukOut || frOut ? '—' : `${delta > 0 ? '+' : ''}${fmtEur(delta)}`}
                  </td>
                  <td style={cell}>{amount(row.uk.breakdown.dividends.ct * 12, ukOut)}</td>
                  <td style={cell}>{amount(row.fr.breakdown.dividends.ct * 12, frOut)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: '0.6875rem', color: 'var(--dim)', margin: 0 }}>
        Scénario France = IS seul (15 % jusqu'à 42 500 € de bénéfice, 25 % au-delà — modifiable dans
        les paramètres). Ne modélise ni les majorations d'une requalification, ni CFE/CVAE, ni un
        statut de dirigeant français. PFU et cotisations identiques des deux côtés.
      </p>
    </section>
  )
}
